import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, CheckCircle, Play, X, TrendingUp, Edit, Filter } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import { hasPermission, isAdmin } from '@/utils/permissions';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { getCurrentRole, getCurrentUser } from '../utils/sessionManager';
import { SearchableItemSelect } from '@/components/ui/searchable-item-select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductionExecutionView = () => {
  const [wos, setWos] = useState([]);
  const [completedWos, setCompletedWos] = useState([]);
  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [completingWO, setCompletingWO] = useState(null);
  const [checkingCompletion, setCheckingCompletion] = useState(false);
  const [completionData, setCompletionData] = useState({
    good_qty: 0,
    scrap_qty: 0,
    warehouse_id: ''
  });
  const [editingWO, setEditingWO] = useState(null);
  const [editForm, setEditForm] = useState({ plan_qty: '' });
  const [showPlanQtyDialog, setShowPlanQtyDialog] = useState(false);
  const [planQtyForm, setPlanQtyForm] = useState({ new_qty: '', remarks: '', reason_id: '', notes: '' });
  const [planQtyWO, setPlanQtyWO] = useState(null);
  const [expandedLog, setExpandedLog] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [qtyChangeReasons, setQtyChangeReasons] = useState([]);
  
  // Filter state
  const [filters, setFilters] = useState({
    prod_date: '',
    item_id: 'all',
    customer_id: 'all'
  });
  
  // Pagination state - active WOs
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    totalCount: 0,
    totalPages: 0
  });
  
  // Pagination state - completed WOs
  const [completedPagination, setCompletedPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0
  });
  const [sortBy, setSortBy] = useState('prod_date');
  const [sortOrder, setSortOrder] = useState('asc');

  const fetchMasterData = async () => {
    try {
      const [itemRes, machRes, whRes, custRes, qcrRes] = await Promise.all([
        axios.get(`${API}/items`),
        axios.get(`${API}/machines`),
        axios.get(`${API}/warehouses`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/qty-change-reasons`)
      ]);
      setItems(itemRes.data);
      setMachines(machRes.data);
      setWarehouses(whRes.data);
      setCustomers(custRes.data);
      setQtyChangeReasons(qcrRes.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchWOs = async () => {
    try {
      // Fetch ACTIVE work orders (PLANNED + RUNNING)
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      // Only fetch non-completed
      params.append('status', 'PLANNED,RUNNING');
      
      // Add server-side filters
      if (filters.prod_date) params.append('prod_date', filters.prod_date);
      if (filters.item_id && filters.item_id !== 'all') params.append('item_id', filters.item_id);
      if (filters.customer_id && filters.customer_id !== 'all') params.append('customer_id', filters.customer_id);
      
      const response = await axios.get(`${API}/production/orders?${params.toString()}`);
      
      if (response.data.data) {
        setWos(response.data.data);
        setPagination(prev => ({
          ...prev,
          totalCount: response.data.pagination.total_count,
          totalPages: response.data.pagination.total_pages
        }));
      } else {
        setWos(response.data);
      }
    } catch (error) {
      console.error('Error fetching work orders:', error);
    }
  };

  const fetchCompletedWOs = async () => {
    try {
      const params = new URLSearchParams({
        page: completedPagination.page.toString(),
        page_size: completedPagination.pageSize.toString(),
        sort_by: 'prod_date',
        sort_order: 'desc',
        status: 'COMPLETED'
      });
      
      if (filters.prod_date) params.append('prod_date', filters.prod_date);
      if (filters.item_id && filters.item_id !== 'all') params.append('item_id', filters.item_id);
      if (filters.customer_id && filters.customer_id !== 'all') params.append('customer_id', filters.customer_id);
      
      const response = await axios.get(`${API}/production/orders?${params.toString()}`);
      
      if (response.data.data) {
        setCompletedWos(response.data.data);
        setCompletedPagination(prev => ({
          ...prev,
          totalCount: response.data.pagination.total_count,
          totalPages: response.data.pagination.total_pages
        }));
      } else {
        setCompletedWos(response.data);
      }
    } catch (error) {
      console.error('Error fetching completed WOs:', error);
    }
  };

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    fetchWOs();
  }, [pagination.page, sortBy, sortOrder, filters]);

  useEffect(() => {
    if (showCompleted) fetchCompletedWOs();
  }, [showCompleted, completedPagination.page, filters]);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };
  
  const clearFilters = () => {
    setFilters({
      prod_date: '',
      item_id: 'all',
      customer_id: 'all'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    setCompletedPagination(prev => ({ ...prev, page: 1 }));
  };

  const getItemInfo = (id) => {
    const item = items.find(i => i.id === id);
    return item ? `${item.part_number} - ${item.description}` : id;
  };

  const getMachineName = (id) => machines.find(m => m.id === id)?.name || 'N/A';

  const getCustomerName = (id) => {
    if (!id) return '-';
    const customer = customers.find(c => c.id === id);
    return customer?.name || '-';
  };

  // Resolve customer from item if not on WO directly
  const getWOCustomer = (wo) => {
    if (wo.customer_id) return getCustomerName(wo.customer_id);
    const item = items.find(i => i.id === wo.item_id);
    return item?.customer_id ? getCustomerName(item.customer_id) : '-';
  };

  const getUserRole = () => {
    try {
      const role = getCurrentRole();
      if (role && role.name) return role.name;
      const user = getCurrentUser();
      if (user && user.role_name) return user.role_name;
    } catch (error) {
      console.error('Error getting user role:', error);
    }
    return '';
  };

  const startProduction = async (woId) => {
    try {
      const userRole = getUserRole();
      await axios.post(`${API}/production/start/${woId}?user_role=${encodeURIComponent(userRole)}`);
      toast.success('Production started successfully! All components are available.');
      fetchWOs();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to start production';
      toast.error(errorMessage, { duration: 8000, style: { maxWidth: '600px' } });
    }
  };

  const openCompleteDialog = async (wo) => {
    setCheckingCompletion(true);
    try {
      const response = await axios.get(`${API}/production/can-complete/${wo.id}`);
      const { canComplete, reason } = response.data;
      
      if (!canComplete) {
        toast.error(reason, { duration: 8000, style: { maxWidth: '500px', whiteSpace: 'pre-wrap' } });
        return;
      }
      
      setCompletingWO(wo);
      setCompletionData({
        good_qty: wo.plan_qty,
        scrap_qty: 0,
        warehouse_id: warehouses.length > 0 ? warehouses[0].id : ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check completion status');
    } finally {
      setCheckingCompletion(false);
    }
  };

  const completeProduction = async () => {
    if (!completingWO) return;

    const total = completionData.good_qty + completionData.scrap_qty;
    if (total !== completingWO.plan_qty) {
      toast.error(`Good qty + Scrap qty must equal planned qty (${completingWO.plan_qty})`);
      return;
    }
    if (completionData.good_qty < 0 || completionData.scrap_qty < 0) {
      toast.error('Quantities cannot be negative');
      return;
    }
    if (!completionData.warehouse_id) {
      toast.error('Please select a warehouse');
      return;
    }

    try {
      await axios.post(`${API}/production/complete/${completingWO.id}`, completionData);
      toast.success(`Production completed! ${completionData.good_qty} good units added to inventory.`);
      setCompletingWO(null);
      fetchWOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete production', { duration: 8000, style: { maxWidth: '600px' } });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PLANNED': return 'bg-blue-100 text-blue-800';
      case 'RUNNING': return 'bg-orange-100 text-orange-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleEditQuantity = (wo) => {
    setPlanQtyWO(wo);
    setPlanQtyForm({ new_qty: wo.plan_qty, remarks: '', reason_id: '', notes: '' });
    setShowPlanQtyDialog(true);
  };

  const canSetZeroQty = () => {
    return isAdmin() || hasPermission('production', 'set_zero_qty');
  };

  const handleSavePlanQty = async () => {
    if (!planQtyForm.reason_id) {
      toast.error('Please select a reason for qty change');
      return;
    }
    const selectedReason = qtyChangeReasons.find(r => r.id === planQtyForm.reason_id);
    const reasonName = selectedReason?.name || '';
    const combinedRemarks = planQtyForm.notes?.trim()
      ? `${reasonName} - ${planQtyForm.notes.trim()}`
      : reasonName;

    const newQty = parseInt(planQtyForm.new_qty);
    if (isNaN(newQty) || newQty < 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    if (newQty === planQtyWO.plan_qty) {
      toast.error('New quantity is same as current quantity');
      return;
    }
    if (newQty === 0 && !canSetZeroQty()) {
      toast.error('You do not have permission to set production quantity to 0');
      return;
    }
    try {
      const user = getCurrentUser() || {};
      const role = getCurrentRole() || {};
      await axios.put(`${API}/production/orders/${planQtyWO.id}/plan-qty`, {
        new_qty: newQty,
        remarks: combinedRemarks,
        changed_by: user.username || user.name || 'admin',
        user_role_id: role.id || ''
      });
      toast.success('Plan quantity updated successfully');
      setShowPlanQtyDialog(false);
      fetchWOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plan quantity');
    }
  };

  const hasActiveFilters = filters.prod_date || filters.item_id !== 'all' || filters.customer_id !== 'all';
  const plannedWOs = wos.filter(w => w.status === 'PLANNED');
  const runningWOs = wos.filter(w => w.status === 'RUNNING');

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Planned Work Orders</p>
                <p className="text-2xl font-bold text-blue-600">{plannedWOs.length}</p>
              </div>
              <Package className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Running</p>
                <p className="text-2xl font-bold text-orange-600">{runningWOs.length}</p>
              </div>
              <Play className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Planned Qty</p>
                <p className="text-2xl font-bold text-slate-900">
                  {Math.round(wos.reduce((sum, w) => sum + w.plan_qty, 0))}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-slate-600" />
          <h3 className="font-semibold text-sm text-slate-700">Filters</h3>
          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={clearFilters} className="ml-auto" data-testid="clear-production-filters">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-medium mb-1 block text-slate-600">Customer</label>
            <Select
              data-testid="production-customer-filter"
              value={filters.customer_id}
              onValueChange={(value) => {
                setFilters({ ...filters, customer_id: value });
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="All customers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All customers</SelectItem>
                {customers.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block text-slate-600">Item</label>
            <SearchableItemSelect
              items={items}
              value={filters.item_id}
              onValueChange={(value) => {
                setFilters({ ...filters, item_id: value });
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              placeholder="All items"
              dataTestId="production-item-filter"
            />
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block text-slate-600">Prod Date</label>
            <Input
              data-testid="production-date-filter"
              type="date"
              value={filters.prod_date}
              onChange={(e) => {
                setFilters({ ...filters, prod_date: e.target.value });
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="h-9"
            />
          </div>
        </div>
      </div>

      <Card data-testid="production-execution-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-slate-600" />
            <div>
              <CardTitle>Production Execution - Complete Work Orders</CardTitle>
              <CardDescription>Start and complete production to update inventory</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {wos.length === 0 ? (
            <div data-testid="no-wos" className="text-center py-8 text-slate-500">
              No active work orders. Run production planning first.
            </div>
          ) : (
            <div className="overflow-auto max-h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>WO Number</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Prod Date</TableHead>
                    <TableHead>Machine</TableHead>
                    <TableHead className="text-right">Plan Qty</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {wos.map((wo) => (
                    <React.Fragment key={wo.id}>
                    <TableRow data-testid={`wo-exec-row-${wo.id}`}>
                      <TableCell className="font-medium">{wo.wo_number}</TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">{getWOCustomer(wo)}</span>
                      </TableCell>
                      <TableCell className="text-sm">{getItemInfo(wo.item_id)}</TableCell>
                      <TableCell>{wo.prod_date}</TableCell>
                      <TableCell>{getMachineName(wo.machine_id)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className="font-semibold">{Math.round(wo.plan_qty)}</span>
                          {wo.status !== 'COMPLETED' && hasPermission('production', 'edit') && (
                            <Button size="sm" variant="ghost" onClick={() => handleEditQuantity(wo)} className="h-8 w-8 p-0" title="Change Plan Quantity" data-testid={`exec-plan-qty-edit-${wo.id}`}>
                              <Edit className="w-4 h-4 text-blue-600" />
                            </Button>
                          )}
                          {wo.qty_change_log?.length > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setExpandedLog(expandedLog === wo.id ? null : wo.id)}
                              className="h-6 px-1.5 py-0 text-[10px] text-amber-600 hover:text-amber-800"
                              title="View change history"
                              data-testid={`exec-change-log-btn-${wo.id}`}
                            >
                              {wo.qty_change_log.length} log{wo.qty_change_log.length > 1 ? 's' : ''}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(wo.status)}>{wo.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {wo.status === 'PLANNED' && hasPermission('production', 'edit') && (
                          <Button size="sm" onClick={() => startProduction(wo.id)} data-testid={`start-wo-${wo.id}`} className="bg-orange-600 hover:bg-orange-700">
                            <Play className="w-4 h-4 mr-1" /> Start
                          </Button>
                        )}
                        {wo.status === 'RUNNING' && hasPermission('production', 'edit') && (
                          <Button size="sm" onClick={() => openCompleteDialog(wo)} disabled={checkingCompletion} data-testid={`complete-wo-${wo.id}`} className="bg-green-600 hover:bg-green-700">
                            <CheckCircle className="w-4 h-4 mr-1" /> {checkingCompletion ? 'Checking...' : 'Complete'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Qty Change History */}
                    {expandedLog === wo.id && wo.qty_change_log?.length > 0 && (
                      <TableRow className="bg-amber-50/50">
                        <TableCell colSpan={8} className="py-2 px-6">
                          <div className="text-xs font-semibold text-amber-800 mb-2 uppercase tracking-wider">Plan Qty Change History</div>
                          <table className="w-full text-xs border border-amber-200 rounded">
                            <thead>
                              <tr className="bg-amber-100/60 text-amber-800">
                                <th className="py-1.5 px-3 text-left">#</th>
                                <th className="py-1.5 px-3 text-left">Date & Time</th>
                                <th className="py-1.5 px-3 text-left">Changed By</th>
                                <th className="py-1.5 px-3 text-right">Old Qty</th>
                                <th className="py-1.5 px-3 text-right">New Qty</th>
                                <th className="py-1.5 px-3 text-left">Remarks</th>
                              </tr>
                            </thead>
                            <tbody>
                              {wo.qty_change_log.map((log, idx) => (
                                <tr key={idx} className="border-t border-amber-100 hover:bg-amber-50" data-testid={`exec-change-log-row-${wo.id}-${idx}`}>
                                  <td className="py-1.5 px-3 text-slate-500">{idx + 1}</td>
                                  <td className="py-1.5 px-3 text-slate-600">{log.changed_at ? new Date(log.changed_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}</td>
                                  <td className="py-1.5 px-3 font-medium text-slate-700">{log.changed_by}</td>
                                  <td className="py-1.5 px-3 text-right text-red-600">{log.old_qty?.toLocaleString()}</td>
                                  <td className="py-1.5 px-3 text-right text-green-600 font-semibold">{log.new_qty?.toLocaleString()}</td>
                                  <td className="py-1.5 px-3 text-slate-600 italic">{log.remarks}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
              
              <PaginationControls pagination={pagination} onPageChange={handlePageChange} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Completed Production Section - Toggle */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <CardTitle>Completed Production</CardTitle>
                <CardDescription>Successfully completed work orders with inventory updated</CardDescription>
              </div>
            </div>
            <Button 
              variant={showCompleted ? "default" : "outline"} 
              size="sm" 
              onClick={() => { setShowCompleted(!showCompleted); if (!showCompleted) fetchCompletedWOs(); }}
              data-testid="toggle-completed-btn"
            >
              {showCompleted ? 'Hide Completed' : 'Show Completed'}
            </Button>
          </div>
        </CardHeader>
        {showCompleted && (
          <CardContent>
            {completedWos.length === 0 ? (
              <div className="text-center py-6 text-slate-500">No completed work orders.</div>
            ) : (
              <div className="overflow-auto max-h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>WO Number</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Prod Date</TableHead>
                      <TableHead>Machine</TableHead>
                      <TableHead className="text-right">Planned</TableHead>
                      <TableHead className="text-right">Good Qty</TableHead>
                      <TableHead className="text-right">Scrap Qty</TableHead>
                      <TableHead>Remarks / Logs</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {completedWos.map((wo) => (
                      <React.Fragment key={wo.id}>
                      <TableRow className="bg-green-50/50">
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {wo.wo_number}
                            {wo.wo_type === 'REWORK' && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-700 border border-orange-200">REWORK</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-600">{getWOCustomer(wo)}</span>
                        </TableCell>
                        <TableCell className="text-sm">{getItemInfo(wo.item_id)}</TableCell>
                        <TableCell>{wo.prod_date}</TableCell>
                        <TableCell>{getMachineName(wo.machine_id)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <span>{Math.round(wo.plan_qty)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold text-green-600">
                          {Math.round(wo.good_qty || 0)}
                        </TableCell>
                        <TableCell className="text-right text-red-600">
                          {Math.round(wo.scrap_qty || 0)}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {wo.notes && (
                              <p className="text-xs text-slate-600 italic">{wo.notes}</p>
                            )}
                            {wo.qty_change_log?.length > 0 && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setExpandedLog(expandedLog === wo.id ? null : wo.id)}
                                className="h-6 px-1.5 py-0 text-[10px] text-amber-600 hover:text-amber-800"
                                title="View change history"
                                data-testid={`completed-change-log-btn-${wo.id}`}
                              >
                                {wo.qty_change_log.length} change log{wo.qty_change_log.length > 1 ? 's' : ''}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedLog === wo.id && wo.qty_change_log?.length > 0 && (
                        <TableRow className="bg-amber-50/50">
                          <TableCell colSpan={9} className="py-2 px-6">
                            <div className="text-xs font-semibold text-amber-800 mb-2 uppercase tracking-wider">Plan Qty Change History</div>
                            <table className="w-full text-xs border border-amber-200 rounded">
                              <thead>
                                <tr className="bg-amber-100/60 text-amber-800">
                                  <th className="py-1.5 px-3 text-left">#</th>
                                  <th className="py-1.5 px-3 text-left">Date & Time</th>
                                  <th className="py-1.5 px-3 text-left">Changed By</th>
                                  <th className="py-1.5 px-3 text-right">Old Qty</th>
                                  <th className="py-1.5 px-3 text-right">New Qty</th>
                                  <th className="py-1.5 px-3 text-left">Remarks</th>
                                </tr>
                              </thead>
                              <tbody>
                                {wo.qty_change_log.map((log, idx) => (
                                  <tr key={idx} className="border-t border-amber-100 hover:bg-amber-50" data-testid={`completed-log-row-${wo.id}-${idx}`}>
                                    <td className="py-1.5 px-3 text-slate-500">{idx + 1}</td>
                                    <td className="py-1.5 px-3 text-slate-600">{log.changed_at ? new Date(log.changed_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}</td>
                                    <td className="py-1.5 px-3 font-medium text-slate-700">{log.changed_by}</td>
                                    <td className="py-1.5 px-3 text-right text-red-600">{log.old_qty?.toLocaleString()}</td>
                                    <td className="py-1.5 px-3 text-right text-green-600 font-semibold">{log.new_qty?.toLocaleString()}</td>
                                    <td className="py-1.5 px-3 text-slate-600 italic">{log.remarks}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
                
                <PaginationControls 
                  pagination={completedPagination} 
                  onPageChange={(newPage) => setCompletedPagination(prev => ({ ...prev, page: newPage }))} 
                />
              </div>
            )}
            {completedWos.length > 0 && (
              <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-800">
                  <strong>Total Completed:</strong> {completedPagination.totalCount} work orders | 
                  <strong> Good Quantity:</strong> {Math.round(completedWos.reduce((sum, w) => sum + (w.good_qty || 0), 0))} units (this page) |
                  <strong> Scrap:</strong> {Math.round(completedWos.reduce((sum, w) => sum + (w.scrap_qty || 0), 0))} units (this page)
                </p>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Completion Dialog */}
      {completingWO && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-[500px]">
            <CardHeader>
              <CardTitle>Complete Production - {completingWO.wo_number}</CardTitle>
              <CardDescription>
                Item: {getItemInfo(completingWO.item_id)}<br/>
                Customer: {getWOCustomer(completingWO)}<br/>
                Planned Qty: {Math.round(completingWO.plan_qty)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="warehouse">Warehouse *</Label>
                  <Select 
                    value={completionData.warehouse_id} 
                    onValueChange={(val) => setCompletionData({ ...completionData, warehouse_id: val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select warehouse" />
                    </SelectTrigger>
                    <SelectContent>
                      {warehouses.map(w => (
                        <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Good Quantity (Added to Inventory) *</label>
                  <Input
                    type="number"
                    value={completionData.good_qty}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      setCompletionData({ ...completionData, good_qty: val, scrap_qty: completingWO.plan_qty - val });
                    }}
                    data-testid="input-good-qty"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Scrap/Rejection Quantity</label>
                  <Input
                    type="number"
                    value={completionData.scrap_qty}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      setCompletionData({ ...completionData, scrap_qty: val, good_qty: completingWO.plan_qty - val });
                    }}
                    data-testid="input-scrap-qty"
                  />
                </div>
                
                <div className={`border rounded p-3 ${
                  (completionData.good_qty + completionData.scrap_qty) === completingWO.plan_qty 
                    ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                }`}>
                  <p className="text-sm font-medium mb-1">Production Summary:</p>
                  <p className="text-sm">
                    Good Qty: <span className="font-semibold text-green-600">{completionData.good_qty}</span> + 
                    Scrap Qty: <span className="font-semibold text-red-600">{completionData.scrap_qty}</span> = 
                    Total: <span className={`font-semibold ml-1 ${
                      (completionData.good_qty + completionData.scrap_qty) === completingWO.plan_qty ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {completionData.good_qty + completionData.scrap_qty}
                    </span>
                    <span className="ml-1">(Must equal {completingWO.plan_qty})</span>
                  </p>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-sm text-blue-900"><strong>Inventory Impact:</strong></p>
                  <p className="text-sm text-blue-800 mt-1">
                    FG: +{completionData.good_qty} units added to inventory<br/>
                    Components: -{completionData.good_qty + completionData.scrap_qty} units consumed (per BOM)
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={completeProduction} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="confirm-complete-btn">
                    <CheckCircle className="w-4 h-4 mr-2" /> Complete & Update Inventory
                  </Button>
                  <Button variant="outline" onClick={() => setCompletingWO(null)} data-testid="cancel-complete-btn">
                    <X className="w-4 h-4 mr-2" /> Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Plan Qty Change Dialog with Mandatory Remarks */}
      <Dialog open={showPlanQtyDialog} onOpenChange={setShowPlanQtyDialog}>
        <DialogContent className="max-w-md" data-testid="exec-plan-qty-dialog">
          <DialogHeader>
            <DialogTitle>Change Plan Quantity</DialogTitle>
            <DialogDescription>
              {planQtyWO && <>WO: <span className="font-semibold text-slate-800">{planQtyWO.wo_number}</span> | Current Qty: <span className="font-bold text-blue-700">{planQtyWO.plan_qty}</span></>}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div>
              <Label htmlFor="exec_new_qty">New Plan Quantity *</Label>
              <Input
                id="exec_new_qty"
                type="number"
                min={canSetZeroQty() ? "0" : "1"}
                step="1"
                value={planQtyForm.new_qty}
                onChange={(e) => setPlanQtyForm({ ...planQtyForm, new_qty: e.target.value })}
                data-testid="exec-plan-qty-new-input"
              />
            </div>
            <div>
              <Label htmlFor="exec_reason">Reason * <span className="text-red-500 text-xs">(mandatory)</span></Label>
              <Select value={planQtyForm.reason_id} onValueChange={v => setPlanQtyForm({ ...planQtyForm, reason_id: v })}>
                <SelectTrigger data-testid="exec-plan-qty-reason-select">
                  <SelectValue placeholder="Select reason for qty change" />
                </SelectTrigger>
                <SelectContent>
                  {qtyChangeReasons.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="exec_notes">Additional Notes <span className="text-slate-400 text-xs">(optional)</span></Label>
              <textarea
                id="exec_notes"
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[60px] resize-none"
                placeholder="Any additional details..."
                value={planQtyForm.notes}
                onChange={(e) => setPlanQtyForm({ ...planQtyForm, notes: e.target.value })}
                data-testid="exec-plan-qty-notes-input"
              />
            </div>

            {planQtyWO?.qty_change_log?.length > 0 && (
              <div className="border border-amber-200 rounded-md p-3 bg-amber-50/50">
                <div className="text-xs font-semibold text-amber-800 mb-2">Previous Changes ({planQtyWO.qty_change_log.length})</div>
                <div className="space-y-1 max-h-[120px] overflow-y-auto">
                  {planQtyWO.qty_change_log.map((log, i) => (
                    <div key={i} className="text-[11px] text-slate-600 flex justify-between gap-2">
                      <span>{log.changed_at ? new Date(log.changed_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}</span>
                      <span className="text-red-500">{log.old_qty}</span>
                      <span>→</span>
                      <span className="text-green-600 font-semibold">{log.new_qty}</span>
                      <span className="italic truncate flex-1">{log.remarks}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanQtyDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSavePlanQty}
              disabled={!planQtyForm.reason_id}
              data-testid="exec-plan-qty-save-btn"
            >
              Update Quantity
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductionExecutionView;
